import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, MessageCircle } from 'lucide-react';
import SEO from '../components/common/SEO';

const Contact = () => {
  const [formState, setFormState] = useState({ name: '', email: '', material: '', quantity: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch("https://formsubmit.co/ajax/rutvikshiroya001@gmail.com", {
        method: "POST",
        headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        body: JSON.stringify({
            name: formState.name,
            email: formState.email,
            material: formState.material,
            quantity: formState.quantity,
            message: formState.message,
            _subject: `New Inquiry from ${formState.name} - AgniThrust`
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
        setFormState({ name: '', email: '', material: '', quantity: '', message: '' });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        alert("Something went wrong while sending your message. Please try again.");
      }
    } catch (error) {
       console.log("Submission error:", error);
       alert("Network error sending message. Please try again later.");
    } finally {
       setIsSubmitting(false);
    }
  };

  const whatsappUrl = `https://wa.me/7621846829?text=Hi AgniThrust, I want to inquire about your bio-energy products.`;

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32 pb-24 px-6 text-left">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-24 items-start">
        
        {/* Contact Info Section */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-16"
        >
          <div>
            <span className="text-primary font-bold tracking-[0.2em] mb-4 block uppercase text-sm">Contact Us</span>
            <h1 className="text-xl sm:text-4xl md:text-6xl font-black syne tracking-tighter mb-8 leading-[0.9]">
              Let's <br />
              <span className="text-white/20 italic">Connect.</span>
            </h1>
            <p className="text-slate-400 text-xl font-medium leading-relaxed max-w-lg">
               Our team is always ready to assist you with any inquiries or custom orders. Feel free to reach out via form, email, or WhatsApp.
            </p>
          </div>

          <div className="space-y-8">
            <div className="flex items-center gap-6 group">
               <div className="w-16 h-16 rounded-3xl bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-black transition-all shadow-xl">
                  <Mail size={28} />
               </div>
               <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Email Address</p>
                  <p className="text-xl font-bold tracking-tight text-white transition-colors group-hover:text-primary">info@agnithrust.com</p>
               </div>
            </div>
            <div className="flex items-center gap-6 group">
               <div className="w-16 h-16 rounded-3xl bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-black transition-all shadow-xl">
                  <Phone size={28} />
               </div>
               <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Phone Number</p>
                  <p className="text-xl font-bold tracking-tight text-white transition-colors group-hover:text-primary">+91 76218 46829</p>
               </div>
            </div>
            <div className="flex items-center gap-6 group">
               <div className="w-16 h-16 rounded-3xl bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-black transition-all shadow-xl">
                  <MapPin size={28} />
               </div>
               <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Our Location</p>
                  <p className="text-lg md:text-xl font-bold tracking-tight text-white transition-colors group-hover:text-primary uppercase leading-tight">
                     A-4 Ramji Industry, Nava Gam,<br />Near Pasodra, Surat, Gujarat 394185
                  </p>
               </div>
            </div>
          </div>

          <a 
            href={whatsappUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-4 px-12 py-6 bg-[#25D366] text-white rounded-3xl font-black syne tracking-widest shadow-2xl hover:bg-[#128C7E] transition-colors"
          >
             <MessageCircle size={28} fill="currentColor" />
             CHAT ON WHATSAPP
          </a>
        </motion.div>

        {/* Contact Form Section */}
        <motion.div
           initial={{ opacity: 0, x: 30 }}
           animate={{ opacity: 1, x: 0 }}
           className="glass p-12 md:p-16 rounded-[60px] border border-white/10 relative overflow-hidden"
        >
           <h3 className="text-3xl font-black syne mb-8 tracking-tight">Send a Message</h3>
           
           <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-2">
                 <label className="text-xs font-bold uppercase tracking-widest text-slate-500 ml-2">Full Name</label>
                 <input 
                   type="text" 
                   required
                   value={formState.name}
                   onChange={e => setFormState({...formState, name: e.target.value})}
                   placeholder="Your name" 
                   className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:outline-none focus:border-primary/50 transition-all font-medium"
                 />
              </div>
              <div className="space-y-2">
                 <label className="text-xs font-bold uppercase tracking-widest text-slate-500 ml-2">Email Address</label>
                 <input 
                   type="email" 
                   required
                   value={formState.email}
                   onChange={e => setFormState({...formState, email: e.target.value})}
                   placeholder="your@email.com" 
                   className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:outline-none focus:border-primary/50 transition-all font-medium"
                 />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                 <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest text-slate-500 ml-2">Select Material</label>
                    <select 
                      required
                      value={formState.material}
                      onChange={e => setFormState({...formState, material: e.target.value})}
                      className="w-full bg-[#1A1A1A] border border-white/10 rounded-2xl px-6 py-4 text-white focus:outline-none focus:border-primary/50 transition-all font-medium"
                    >
                       <option value="" disabled>Choose Product...</option>
                       <option value="Bio Mass Pellets">Bio Mass Pellets</option>
                       <option value="Bio Coal (Briquettes)">Bio Coal (Briquettes)</option>
                    </select>
                 </div>
                 <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest text-slate-500 ml-2">Quantity</label>
                    <input 
                      type="text" 
                      required
                      value={formState.quantity}
                      onChange={e => setFormState({...formState, quantity: e.target.value})}
                      placeholder="e.g. 50 Tons" 
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:outline-none focus:border-primary/50 transition-all font-medium"
                    />
                 </div>
              </div>

              <div className="space-y-2">
                 <label className="text-xs font-bold uppercase tracking-widest text-slate-500 ml-2">Your Message</label>
                 <textarea 
                   rows="4"
                   required
                   value={formState.message}
                   onChange={e => setFormState({...formState, message: e.target.value})}
                   placeholder="Tell us about your requirements..." 
                   className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:outline-none focus:border-primary/50 transition-all font-medium resize-none"
                 />
              </div>
              
              <button 
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-6 rounded-2xl font-black syne tracking-widest text-sm transition-all shadow-2xl flex items-center justify-center gap-3 active:scale-95 ${isSubmitting ? 'bg-primary/50 text-white/50 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark text-white'}`}
              >
                 {isSubmitting ? 'SENDING...' : (submitted ? 'MESSAGE SENT!' : 'SEND INQUIRY')}
                 <Send size={18} />
              </button>
           </form>

           {submitted && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 text-center bg-primary/20 p-4 rounded-xl border border-primary/20"
              >
                 <p className="text-primary font-bold text-sm tracking-widest uppercase italic leading-none">
                    Thank you! Your inquiry has been sent to info@agnithrust.com.
                 </p>
              </motion.div>
           )}
        </motion.div>

      </div>
    </div>
  );
};

export default Contact;
